/**
 * useNotificationState — Local state management for notification read/unread status.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/utils/logger';

const STORAGE_KEY = "goal_tracker_deadline_acknowledgments";

interface AcknowledgedNotification {
  id: string;
  timestamp: number;
  deadlineDate?: string; // Store the deadline date to expire acknowledgment after deadline passes
}

// Helper to read from localStorage synchronously
const readFromStorage = (): AcknowledgedNotification[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    logger.error("Failed to read acknowledged notifications:", e);
    return [];
  }
};

// Helper to write to localStorage synchronously
const writeToStorage = (notifications: AcknowledgedNotification[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  } catch (e) {
    logger.error("Failed to save acknowledged notifications:", e);
  }
};

export const useNotificationState = () => {
  const [acknowledgedNotifications, setAcknowledgedNotifications] = useState<AcknowledgedNotification[]>(() => {
    // Initialize from localStorage synchronously
    return readFromStorage();
  });

  // Use ref to prevent stale closures
  const acknowledgedRef = useRef(acknowledgedNotifications);
  acknowledgedRef.current = acknowledgedNotifications;

  // Cleanup expired acknowledgments (deadlines that have passed)
  useEffect(() => {
    const cleanup = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const updated = acknowledgedNotifications.filter(n => {
        // If no deadline date stored, keep for 7 days
        if (!n.deadlineDate) {
          const sevenDays = 7 * 24 * 60 * 60 * 1000;
          return (Date.now() - n.timestamp) < sevenDays;
        }
        // If deadline has passed, remove the acknowledgment
        const deadline = new Date(n.deadlineDate);
        deadline.setHours(0, 0, 0, 0);
        return deadline >= today;
      });

      if (updated.length !== acknowledgedNotifications.length) {
        setAcknowledgedNotifications(updated);
        writeToStorage(updated);
      }
    };

    cleanup();
    // Run cleanup once per hour
    const cleanupInterval = setInterval(cleanup, 60 * 60 * 1000);
    return () => clearInterval(cleanupInterval);
  }, [acknowledgedNotifications]);

  const hasAcknowledged = useCallback((notificationId: string) => {
    // Always read fresh from storage to ensure consistency
    const stored = readFromStorage();
    return stored.some(n => n.id === notificationId);
  }, []);

  const acknowledgeNotification = useCallback((notificationId: string, deadlineDate?: string) => {
    // Read fresh, update, and write synchronously
    const current = readFromStorage();
    const exists = current.some(n => n.id === notificationId);
    
    let updated: AcknowledgedNotification[];
    if (exists) {
      updated = current.map(n => 
        n.id === notificationId 
          ? { ...n, timestamp: Date.now(), deadlineDate: deadlineDate || n.deadlineDate } 
          : n
      );
    } else {
      updated = [...current, { id: notificationId, timestamp: Date.now(), deadlineDate }];
    }
    
    // Write to localStorage immediately
    writeToStorage(updated);
    // Then update state
    setAcknowledgedNotifications(updated);
  }, []);

  return {
    hasAcknowledged,
    acknowledgeNotification
  };
};
